import React, { useMemo, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "./ui/sheet";
import { BookOpen, Trash2, Volume2, Bookmark, Plus, X, Globe } from "lucide-react";
import {
  CATEGORIES,
  getFavorites,
  getCached,
  removeFavorite,
  getCustomPhrases,
  addCustomPhrase,
  removeCustomPhrase,
  isValidTranslation,
} from "../lib/phrasebook";
import { getOfflineTranslation } from "../lib/offlinePhrasebook";
import { confirmDelete } from "../lib/confirmDelete";

// Onus: parent provides onUse(text) which will translate & inject into convo.
// onFavoriteUse plays favorite audio from cache if present else calls onUse with source text.
export default function PhraseBook({
  myLang,
  theirLang,
  provider,
  onUse,
  onPlayFavorite,
  refreshKey,
}) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("common");
  const [customRefresh, setCustomRefresh] = useState(0);

  const favorites = useMemo(() => getFavorites(), [refreshKey, open]);

  const handleUse = (text) => {
    onUse(text);
    setOpen(false);
  };

  const bumpCustom = () => setCustomRefresh((n) => n + 1);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          data-testid="phrasebook-open"
          className="flex items-center gap-2 px-4 py-2 bg-white border border-navy/15 rounded-full shadow-soft text-xs uppercase tracking-widest font-jakarta font-extrabold text-navy hover:bg-bone transition-colors"
        >
          <BookOpen size={14} strokeWidth={2.6} />
          Phrasebook
        </button>
      </SheetTrigger>
      <SheetContent
        side="bottom"
        className="h-[95vh] bg-white border-none rounded-t-3xl p-0 overflow-hidden max-w-md mx-auto"
      >
        <SheetHeader className="px-5 pt-5 pb-3 bg-white">
          <div className="w-12 h-1.5 bg-navy/15 rounded-full mx-auto mb-3" />
          <SheetTitle className="font-jakarta font-extrabold text-3xl text-navy text-center">
            Phrase<span className="text-orange">book</span>
          </SheetTitle>
          <p className="text-sm text-navy-soft font-jakarta font-semibold text-center mt-1">
            Tap to translate into {theirLang}
          </p>
        </SheetHeader>

        {/* Marketing strip — surfaces the "every phrase, every language,
            no quota" guarantee that ships from the new server-side seed
            phrase cache. Free users see this as a permanent perk; Pro
            framing on the rest of the app pivots to "your own phrases".
            Designed to be tasteful: single line, low-contrast bg, no
            CTA so it doesn't compete with the phrase list below. */}
        <div
          data-testid="phrasebook-marketing-strip"
          className="mx-5 mb-3 flex items-center gap-2 px-3 py-2 rounded-xl bg-forest/8 border border-forest/15"
        >
          <div className="w-7 h-7 rounded-full bg-forest flex items-center justify-center shrink-0">
            <Globe size={14} strokeWidth={2.6} color="#fff" />
          </div>
          <div className="font-jakarta text-[12px] text-navy leading-snug">
            <span className="font-bold">Free in 50+ languages</span> · tap
            any phrase to hear it — even when you&apos;re offline or out of
            credits.
          </div>
        </div>

        <div className="flex border-b border-navy/10 bg-white">
          <TabBtn
            active={tab === "common"}
            onClick={() => setTab("common")}
            testId="phrasebook-tab-common"
          >
            Common
          </TabBtn>
          <TabBtn
            active={tab === "favorites"}
            onClick={() => setTab("favorites")}
            testId="phrasebook-tab-favorites"
          >
            Favorites{" "}
            <span className="ml-1 text-[10px]">
              ({favorites.length})
            </span>
          </TabBtn>
        </div>

        <div className="overflow-y-auto h-[calc(95vh-140px)] px-5 py-4">
          {tab === "common" ? (
            <div className="space-y-5">
              {CATEGORIES.map((cat) => (
                <CategorySection
                  key={cat.id}
                  category={cat}
                  theirLang={theirLang}
                  provider={provider}
                  onUse={handleUse}
                  customRefresh={customRefresh}
                  bumpCustom={bumpCustom}
                />
              ))}
              <p className="text-[10px] text-muted2 font-bricolage italic pt-2 flex items-center gap-1">
                <Bookmark size={11} className="inline text-orange" fill="#E55B2C" strokeWidth={2} />
                = cached offline · tap any phrase to translate &amp; speak
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {favorites.length === 0 && (
                <div className="text-center text-muted2 font-bricolage text-sm py-12">
                  No favorites yet.
                  <br />
                  <span className="text-xs">
                    Tap the ♥ on any translation to save it here.
                  </span>
                </div>
              )}
              {favorites.map((f) => (
                <div
                  key={f.id}
                  data-testid={`favorite-item`}
                  className="bg-white border-2 border-ink rounded-sm p-3 shadow-stamp-sm flex items-start gap-2"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-muted2 font-bricolage mb-1">
                      {f.src} → {f.tgt}
                    </div>
                    <div className="font-bricolage text-[13px] text-muted2 italic truncate">
                      {f.source}
                    </div>
                    {isValidTranslation(f.translation, f.source, f.tgt) ? (
                      <div className="font-fraunces text-base text-ink leading-snug">
                        {f.translation}
                      </div>
                    ) : (
                      <div className="font-fraunces text-[13px] text-muted2 italic leading-snug">
                        Tap to retranslate
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    <button
                      onClick={() => {
                        onPlayFavorite(f);
                      }}
                      aria-label="Play"
                      className="w-8 h-8 rounded-full bg-forest text-bone border-2 border-ink flex items-center justify-center shadow-stamp-sm hover:bg-ink transition-colors"
                    >
                      <Volume2 size={12} strokeWidth={2.5} />
                    </button>
                    <button
                      onClick={() => {
                        removeFavorite(f.id);
                        // trigger rerender
                        setTab("favorites");
                        setOpen(true);
                        // force refresh via state change
                        window.dispatchEvent(new Event("voyago-fav-change"));
                      }}
                      aria-label="Remove"
                      className="w-8 h-8 rounded-full bg-white text-ink border-2 border-ink flex items-center justify-center hover:bg-cream transition-colors"
                    >
                      <Trash2 size={12} strokeWidth={2.5} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function TabBtn({ active, onClick, children, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`flex-1 font-jakarta font-bold text-base py-3 px-2 transition-colors border-b-2 -mb-[2px] ${
        active
          ? "text-orange border-orange"
          : "text-navy-soft border-transparent hover:text-navy"
      }`}
    >
      {children}
    </button>
  );
}

function CategorySection({
  category,
  theirLang,
  provider,
  onUse,
  customRefresh,
  bumpCustom,
}) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  // refresh-keyed read so re-renders pick up changes
  const customs = useMemo(
    () => getCustomPhrases(category.id),
    [category.id, customRefresh]
  );

  const submit = () => {
    const text = draft.trim();
    if (!text) {
      setAdding(false);
      return;
    }
    addCustomPhrase(category.id, text);
    setDraft("");
    setAdding(false);
    bumpCustom();
  };

  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.25em] text-muted2 font-bricolage mb-2 flex items-center gap-2">
        <span className="h-px flex-1 bg-line" />
        {category.label}
        <span className="h-px flex-1 bg-line" />
      </div>
      <div className="flex flex-col gap-2">
        {category.phrases.map((p) => (
          <PhraseRow
            key={p}
            text={p}
            theirLang={theirLang}
            provider={provider}
            onUse={onUse}
            testId={`phrase-${category.id}-${p.slice(0, 12)}`}
          />
        ))}

        {customs.map((c) => (
          <PhraseRow
            key={c.id}
            text={c.text}
            theirLang={theirLang}
            provider={provider}
            onUse={onUse}
            isCustom
            onDelete={async () => {
              if (!(await confirmDelete())) return;
              removeCustomPhrase(category.id, c.id);
              bumpCustom();
            }}
            testId={`custom-phrase-${category.id}-${c.id}`}
          />
        ))}

        {adding ? (
          <div
            data-testid={`custom-add-form-${category.id}`}
            className="flex items-center gap-2 bg-cream border-2 border-dashed border-ink rounded-sm p-2"
          >
            <input
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") submit();
                if (e.key === "Escape") {
                  setAdding(false);
                  setDraft("");
                }
              }}
              placeholder={`e.g., "I'm allergic to peanuts"`}
              className="flex-1 bg-transparent border-b-2 border-ink rounded-none px-1 py-1 font-bricolage text-sm focus:outline-none focus:border-terracotta placeholder:text-muted2"
              data-testid={`custom-add-input-${category.id}`}
            />
            <button
              type="button"
              onClick={submit}
              data-testid={`custom-add-save-${category.id}`}
              className="bg-forest text-bone border-2 border-ink rounded-sm px-3 py-1.5 text-[10px] uppercase tracking-widest font-bricolage font-bold shadow-stamp-sm hover:bg-ink"
            >
              Add
            </button>
            <button
              type="button"
              onClick={() => {
                setAdding(false);
                setDraft("");
              }}
              className="w-7 h-7 rounded-full bg-white border-2 border-ink flex items-center justify-center hover:bg-sand"
              aria-label="Cancel"
            >
              <X size={11} strokeWidth={2.5} />
            </button>
          </div>
        ) : (
          <button
            type="button"
            data-testid={`custom-add-button-${category.id}`}
            onClick={() => setAdding(true)}
            className="flex items-center gap-1 self-start text-[10px] uppercase tracking-widest font-bricolage font-semibold text-muted2 border-2 border-dashed border-line rounded-sm px-3 py-1.5 hover:bg-cream hover:text-terracotta hover:border-terracotta transition-colors"
          >
            <Plus size={11} strokeWidth={3} />
            Add your own
          </button>
        )}
      </div>
    </div>
  );
}

function PhraseRow({ text, theirLang, provider, onUse, isCustom, onDelete, testId }) {
  // Prefer the curated offline translation (always-correct) over the
  // localStorage cache (may contain stale bad entries from earlier
  // API failures). Falls back to online cache for phrases that aren't
  // in the offline list (e.g. custom phrases, less-common categories).
  // Cached entries are validated against the target script — stale
  // English-as-Japanese rows are filtered out so users see the source
  // only and can tap to refetch.
  const offline = getOfflineTranslation(text, "English", theirLang);
  let cached = offline || getCached(text, "English", theirLang, provider);
  if (cached && !isValidTranslation(cached.translation, text, theirLang)) {
    cached = null;
  }
  return (
    <div className="relative">
      <button
        data-testid={testId}
        onClick={() => onUse(text)}
        className={`w-full text-left bg-white border-2 border-ink rounded-sm px-3 py-2 shadow-stamp-sm hover:bg-sand transition-colors flex items-start justify-between gap-2 ${
          isCustom ? "pr-9" : ""
        }`}
      >
        <div className="flex-1">
          <div className="font-bricolage text-sm text-ink flex items-center gap-1.5">
            {isCustom && (
              <span className="text-[8px] font-mono uppercase tracking-widest bg-sand text-ink px-1 py-0.5 rounded-sm border border-ink/40">
                You
              </span>
            )}
            <span>{text}</span>
          </div>
          {cached && (
            <div className="font-fraunces italic text-[13px] text-forest mt-0.5">
              {cached.translation}
            </div>
          )}
        </div>
        {cached ? (
          <Bookmark
            size={16}
            className="text-orange mt-1 shrink-0"
            fill="#E55B2C"
            strokeWidth={2}
          />
        ) : null}
      </button>
      {isCustom && onDelete && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          aria-label="Delete custom phrase"
          data-testid={`custom-phrase-delete`}
          className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-white border-2 border-ink flex items-center justify-center hover:bg-cream"
        >
          <Trash2 size={11} strokeWidth={2.5} />
        </button>
      )}
    </div>
  );
}
